import React, { PureComponent } from 'react';
import ReactDOMServer from 'react-dom/server';
import moment from 'moment';
import { compact, memoize, pick } from 'lodash/fp';
import { connect } from 'react-redux';
import Select from 'components/shared/select';
import BootstrapTooltip from 'components/shared/BootstrapTooltip';
import * as selectors from 'selectors/assign';
import { actions, updateExistingAssignmentOption } from 'actions/assign';
import { pluralize } from 'utils/string_utils';
import openPopup from 'utils/open_popup';
import request from 'utils/request_ajax';
import DateFormat from 'constants/date_format';
import { routes } from 'constants/config';
// eslint-disable-next-line import/no-cycle
import MobileReview from './ReviewMobile';

export const formatDate = date => moment(date).format(DateFormat.LONG);
export const formatTime = time => moment(time, 'HH:mm').format('hh:mm A');

const JourneyRestartReminders = ({ showTitle }) => {
  return (
    <div className="FormGroup FormGroup--light">
      <div className="AssignModal-assignmentOption">
        {showTitle && (
          <div className="AssignModal-assignmentOptionTitle">
            Learning Journeys
          </div>
        )}
        <span>People who already have this content assigned</span>
        <span> will not receive a reminder.</span>
      </div>
      <div className="AssignModal-assignmentOption">
        <span>People who have already started any of this content</span>
        <span> will not be restarted.</span>
        <BootstrapTooltip
          className="ml-12"
          tooltip="Data of the users who have fully completed any of this content will be retained for reporting purposes."
          dataPlacement="top"
          iconName="info"
          iconClasses="h-c-gray-darker"
        />
      </div>
    </div>
  );
};

const TrainingContentRestartReminders = ({
  showTitle,
  sendReminder,
  reassignCompleted,
  handleAssignmentOptionChange,
}) => {
  return (
    <div className="FormGroup FormGroup--light">
      <div className="AssignModal-assignmentOption">
        {showTitle && (
          <div className="AssignModal-assignmentOptionTitle">
            Training Content
            <BootstrapTooltip
              tooltip="Training Content includes selected Lessons, Paths, and Resources."
              className="ml-12"
              dataPlacement="top"
              iconName="info"
              iconClasses="h-c-gray-darker"
            />
          </div>
        )}
        <span>People who already have this content assigned</span>

        <Select
          value={sendReminder}
          name="sendReminder"
          onChange={handleAssignmentOptionChange}
        >
          <option value={true}>will receive a reminder</option>
          <option value={false}>will not receive a reminder</option>
        </Select>
      </div>
      <div className="AssignModal-assignmentOption">
        <span>People who have already started any of this content</span>
        <Select
          value={reassignCompleted}
          name="reassignCompleted"
          onChange={handleAssignmentOptionChange}
        >
          <option value={true}>will be restarted</option>
          <option value={false}>will not be restarted</option>
        </Select>
        <BootstrapTooltip
          className="ml-12"
          tooltip="Data of the users who have fully completed any of this content will be retained for reporting purposes."
          dataPlacement="top"
          iconName="info"
          iconClasses="h-c-gray-darker"
        />
      </div>
    </div>
  );
};

class ReviewView extends PureComponent {
  constructor(props) {
    super(props);
    this.renderStaticTooltip = this.renderStaticTooltip.bind(this);
    this.parseTokens = memoize(this.parseTokens);
    this.handleChangeMessage = this.handleChangeMessage.bind(this);
    this.handlePreviewEmail = this.handlePreviewEmail.bind(this);
    this.handleAssignmentOptionChange =
      this.handleAssignmentOptionChange.bind(this);
  }

  renderStaticTooltip() {
    const smartGroupsArray = this.props.assigneesArray.map(
      assignee => assignee.meta.isSmartGroup,
    );
    const hasSmartGroup = smartGroupsArray.includes(true);

    if (hasSmartGroup) {
      return ReactDOMServer.renderToStaticMarkup(
        <BootstrapTooltip
          className="ml-4"
          tooltip={this.props.smartGroupsTooltip}
          dataPlacement="top"
          iconName="info"
          iconClasses="h-c-gray-darker"
        />,
      );
    }

    return '';
  }

  get assignmentSummaryHTML() {
    const {
      usersCount,
      groupsCount,
      uniqueUsersCount,
      assignDate,
      assignTime,
      timeZone,
      dueDate,
      mixedAssignees,
      assignment,
      journeyAssignment,
      recurring,
    } = this.props;

    const hasMultipleAssignmentTypes =
      journeyAssignment.content.length > 0 && (assignDate || recurring);

    const contentCount = hasMultipleAssignmentTypes
      ? assignment.content.length
      : journeyAssignment.content.length + assignment.content.length;

    const textWithFeatureFlag =
      assignTime && assignDate && timeZone
        ? `on __${formatDate(assignDate)}__ at __${formatTime(
            assignTime,
          )} ${timeZone}__`
        : `on __${formatDate(assignDate)}__`;

    const summary = compact([
      `__${contentCount} ${pluralize(contentCount, 'item')}__ will be assigned`,
      assignDate ? textWithFeatureFlag : '__immediately__',
      'to',
      usersCount &&
        `__${usersCount} ${pluralize(usersCount, 'person', 'people')}__`,
      mixedAssignees && 'and',
      groupsCount && `__${groupsCount} ${pluralize(groupsCount, 'group')}__`,
      (mixedAssignees || !!groupsCount) &&
        `(${uniqueUsersCount} total people${this.renderStaticTooltip()})`,
      dueDate && `with a due date of __${formatDate(dueDate)}__`,
    ])
      .join(' ')
      .concat('.');

    return this.parseTokens(summary);
  }

  // Display if journey assignments differ from training content assignments
  get secondaryAssignmentSummerHTML() {
    const {
      usersCount,
      groupsCount,
      uniqueUsersCount,
      dueDate,
      mixedAssignees,
    } = this.props;

    const contentCount = this.props.journeyAssignment.content.length;

    const summary = compact([
      `__${contentCount} ${pluralize(contentCount, 'item')}__ will be assigned`,
      '__immediately__',
      'to',
      usersCount &&
        `__${usersCount} ${pluralize(usersCount, 'person', 'people')}__`,
      mixedAssignees && 'and',
      groupsCount && `__${groupsCount} ${pluralize(groupsCount, 'group')}__`,
      (mixedAssignees || !!groupsCount) &&
        `(${uniqueUsersCount} total people${this.renderStaticTooltip()})`,
      dueDate && `with a due date of __${formatDate(dueDate)}__`,
    ])
      .join(' ')
      .concat('.');

    return this.parseTokens(summary);
  }

  get recurringAssignmentSummaryHTML() {
    const { recurringOptions, assignment } = this.props;
    const { assignOnAmount, assignOnUnit, dueDateAmount, dueDateUnit } =
      recurringOptions;

    const contentCount = assignment.content.length;

    const summary = compact([
      `The same ${pluralize(contentCount, 'item')} will be reassigned`,
      `__every ${assignOnAmount} ${pluralize(
        assignOnAmount,
        assignOnUnit.slice(0, -1),
      )}__`,
      dueDateAmount &&
        dueDateUnit &&
        `and __due within ${dueDateAmount} ${pluralize(
          dueDateAmount,
          dueDateUnit.slice(0, -1),
        )}__`,
    ])
      .join(' ')
      .concat('.');

    return this.parseTokens(summary);
  }

  parseTokens(body) {
    const highlightTag = (strings, ...values) => `<b>${values[0]}</b>`;

    const highlightReplacer = (highlight, userName) =>
      highlightTag`${userName}`;
    return body.replace(/__(.+?)__/g, highlightReplacer);
  }

  handleChangeMessage() {
    this.props.updateMessage(this.messageInput.value);
  }

  handlePreviewEmail() {
    openPopup({ name: 'email_preview' }).then(popup => {
      const getAssignmentData = pick([
        'message',
        'content',
        'schedule.dueDate',
      ]);
      const assignment = getAssignmentData(this.props.assignment);
      request
        .postJSON(routes.EMAIL_PREVIEW, assignment, { dataType: 'html' })
        .then(html => {
          const css = popup.document.createElement('style');
          css.innerHTML =
            "body { padding: 5%; background: rgb(244, 244, 244); font: normal 13px/1 'proxima-nova', 'Helvetica Neue', sans-serif;} .maincontain { box-shadow: 0px 1px 1px 0px #ececec; }";
          popup.document.body.innerHTML = html;
          popup.document.body.appendChild(css);
        });
    });
  }

  handleAssignmentOptionChange(e) {
    const { name, value } = e.target;
    this.props.updateExistingAssignmentOption({ [name]: value === 'true' });
  }

  render() {
    const {
      existingAssignments: { sendReminder, reassignCompleted },
      assignment,
      assignDate,
      recurring,
      journeyAssignment,
    } = this.props;

    return (
      <div className="AssignModal-detailesView">
        <div className="FormGroup FormGroup--light">
          <MobileReview />
          <div className="AssignModal-reviewSummary u-hideBelowXS">
            <span
              dangerouslySetInnerHTML={{ __html: this.assignmentSummaryHTML }}
            />
            &nbsp;&nbsp;
            {recurring && (
              <span
                dangerouslySetInnerHTML={{
                  __html: this.recurringAssignmentSummaryHTML,
                }}
              />
            )}
            {journeyAssignment.content.length > 0 && (assignDate || recurring) && (
              <div>
                <p
                  dangerouslySetInnerHTML={{
                    __html: this.secondaryAssignmentSummerHTML,
                  }}
                />
              </div>
            )}
          </div>
        </div>
        {assignment.content.length > 0 &&
          journeyAssignment.content.length === 0 && (
            <TrainingContentRestartReminders
              showTitle={false}
              sendReminder={sendReminder}
              reassignCompleted={reassignCompleted}
              handleAssignmentOptionChange={this.handleAssignmentOptionChange}
            />
          )}
        {journeyAssignment.content.length > 0 &&
          assignment.content.length === 0 && (
            <JourneyRestartReminders showTitle={false} />
          )}
        <div className="FormGroup FormGroup--light">
          <div className="AssignModal-formBlockHeader">
            <label
              htmlFor="AssignModal-messageInput"
              className="AssignModal-subtitle llui-text-body-bold"
            >
              {journeyAssignment.content.length > 0 &&
              assignment.content.length > 0 ? (
                <>
                  Optional message to send with all emails for this assignment
                </>
              ) : (
                <>Optional Assignment Email Message</>
              )}
            </label>
            <button
              type="button"
              className="AssignModal-previewEmailBtn u-linkBtn c-brand-blue"
              onClick={this.handlePreviewEmail}
              disabled={!this.props.message.trim().length}
            >
              Preview Email
            </button>
          </div>
          <textarea
            id="AssignModal-messageInput"
            className="AssignModal-messageInput"
            ref={node => {
              this.messageInput = node;
            }}
            value={this.props.message}
            onChange={this.handleChangeMessage}
          />
        </div>
        {journeyAssignment.content.length > 0 && assignment.content.length > 0 && (
          <div className="AssignModal-additionalSettings">
            <div className="AssignModal-additionalSettingsText">
              Additional settings
            </div>
            <TrainingContentRestartReminders
              showTitle
              sendReminder={sendReminder}
              reassignCompleted={reassignCompleted}
              handleAssignmentOptionChange={this.handleAssignmentOptionChange}
            />
            <JourneyRestartReminders showTitle />
          </div>
        )}
      </div>
    );
  }
}

const mapStateToProps = state => ({
  assigneesArray: state.selection.assignees.data,
  usersCount: selectors.getSelectedUsersCount(state),
  groupsCount: selectors.getSelectedGroupsCount(state),
  uniqueUsersCount: state.selection.assignees.count,
  mixedAssignees: selectors.hasSelectedMixedAssigneeTypes(state),
  assignDate: state.schedule.assignDate,
  assignTime: state.schedule.assignTime,
  timeZone: state.schedule.timeZone,
  dueDate: state.schedule.dueDate,
  recurring: state.schedule.recurring,
  recurringOptions: state.schedule.recurringOptions,
  existingAssignments: state.existingAssignments,
  message: state.message,
  assignment: selectors.buildAssignment(state),
  journeyAssignment: selectors.buildJourneyAssignment(state),
  smartGroupsTooltip: state.meta.i18n.smart_groups_calculation_tooltip,
});

const mapDispatchToProps = {
  updateMessage: actions.updateMessage,
  updateExistingAssignmentOption,
};

export default connect(mapStateToProps, mapDispatchToProps)(ReviewView);
